<template>
  <div
    v-if="activeArticle"
    class="blog-article">
    <div class="blog-article__image-wrap">
      <figure
        v-if="img && !isXurArticle"
        :class="{ 'blog-article__image--has-caption': activeArticle.image_caption }"
        class="blog-article__image">
        <responsive-image
          :image-data="img"
          fill
          class="blog-article__image-pic" />
        <figcaption
          v-if="activeArticle.image_caption"
          class="blog-article__image-caption">
          {{ activeArticle.image_caption }}
        </figcaption>
      </figure>

      <altcraft-banner-xur v-if="isXurArticle" />
    </div>

    <!-- eslint-disable vue/no-v-html -->
    <div class="blog-article__body blog-article__content">
      <div class="blog-article__body blog-article__content">
        <template v-for="(chunk, index) in parsedContent">
          <div
            v-if="chunk.type === 'our-products' && activeArticle.our_products && activeArticle.products"
            :key="`${index}-products`"
            class="blog-article__our-products">
            <div
              class="blog-article__html our-products__content"
              v-html="chunk.content" />

            <ul class="our-products__list">
              <product-card
                v-for="product in chunk.products"
                :key="product.id"
                :product="product"
                :link-query="addLinkQuery(product)"
                class="our-products__list-item" />
            </ul>
          </div>

          <div
            v-else-if="chunk.type === 'html'"
            :key="`${index}-html`"
            class="blog-article__html"
            v-html="cleanHTML(chunk.content)" />

          <blog-checklist
            v-else-if="chunk.type === 'checklist'"
            :key="`${index}-checklist`"
            :checklist-items="chunk.checklistItems" />
        </template>
      </div>
    </div>

    <h2 class="blog-article__title">
      Did you like the article?
    </h2>

    <ui-rate
      :value="rate.value"
      :is-read-only="rate.isReadOnly"
      title="Rate it!"
      class="blog-article__rate"
      @input="setRate" />

    <template v-if="activeArticle.related_blog_posts && activeArticle.related_blog_posts.length > 0">
      <h2 class="blog-article__title">
        You may also like
      </h2>

      <base-card-list
        :items="relatedArticles"
        :visible-rows="1"
        :scrollable-from="1279"
        class="blog-article__you-may-like">
        <template #card="{ item, isMobile }">
          <article-card
            :is-full="!isMobile"
            :article="item" />
        </template>
      </base-card-list>
    </template>
  </div>
</template>

<script>
import { mapMutations, mapState } from 'vuex';
import axios from 'axios';
import AppModel from 'Models/AppModel';
import GameModel from 'Models/GameModel';
import UiRate from 'Components/ui/Rating/UiRate.vue';
import ProductCard from 'Root/pages/game/components/ProductCard.vue';
import ResponsiveImage from 'Components/ui/Image/ResponsiveImage.vue';
import seoMixin from 'Root/mixins/seoMixin';
import { executeAfterLoad, setNotFoundState } from 'Root/helpers/tools';
import { regExpLightGGLink, regExpWowHeadLink } from 'Root/helpers/regexps';
import tooltips from 'Root/mixins/tooltips';
import tinymceComponents from 'Root/mixins/tinymceComponents';

import BaseCardList from 'Components/ui/BaseCardList/BaseCardList.vue';
import AltcraftBannerXur from 'Components/Popups/altcraftPopups/AltcraftBannerXur.vue';
import ArticleCard from 'Root/pages/blog/components/ArticleCard.vue';
import BlogChecklist from 'Root/pages/blog/components/BlogChecklist.vue';
import { htmlNormalizeImageRatio } from '../../../helpers/htmlNormalizer';

const { API_URI } = AppModel;

const LOCALSTORAGE_RATES_KEY = 'POST_RATES_BY_SLUG';
let localStorageRatesBySlug;

function setHeaderInfo(store) {
  if (store.state.blog.activeArticle) {
    store.commit('blog/mutateEntity', {
      entity: 'headerInfo',
      data: {
        title: store.state.blog.activeArticle.title,
        description: store.state.blog.activeArticle.description,
        component: {
          component: 'ArticleStats',
          props: {
            article: store.state.blog.activeArticle,
          },
        },
      },
    });
  }
}

function setArticleInfo(store, article) {
  store.commit('blog/mutateEntity', {
    entity: 'activeCategory',
    data: article.category || null,
  });

  store.commit('blog/mutateEntity', {
    entity: 'activeArticle',
    data: article,
  });

  store.commit('products/mutateEntity', {
    entity: 'popularHeaderLinks',
    data: article.popular_links || [],
  });

  setHeaderInfo(store);
}

function getArticle(store, slug) {
  return axios.get(`${AppModel.API_URI}/blog_posts/${slug}`)
    .then(({ data }) => {
      setArticleInfo(store, data);
    });
}

export default {
  name: 'BlogArticle',

  components: {
    BlogChecklist,
    ProductCard,
    UiRate,
    ResponsiveImage,
    AltcraftBannerXur,

    BaseCardList,
    ArticleCard,
  },

  mixins: [
    seoMixin,
    tooltips,
    tinymceComponents,
  ],

  beforeRouteUpdate(to, from, next) {
    getArticle(this.$store, to.params.articleSlug)
      .then(() => {
        this.setFilter();
        this.incrementViews();
      });

    next();
  },

  beforeRouteLeave(to, from, next) {
    this.mutateEntity({ entity: 'activeArticle', data: null });
    this.mutateProductsEntity({ entity: 'popularHeaderLinks', data: [] });
    next();
  },

  asyncData({ store, router, context }) {
    if (context._is404) {
      return Promise.resolve();
    }

    const articlePromise = getArticle(store, router.currentRoute.params.articleSlug)
      .then(() => {
        if (store.state.blog.activeArticle.deleted_at) {
          setNotFoundState(store, context);
        }

        return Promise.resolve();
      })
      .catch(() => {
        setNotFoundState(store, context);
        return Promise.resolve();
      });

    return Promise.all([articlePromise]);
  },

  data() {
    return {
      rate: {
        value: null,
        isReadOnly: false,
      },
    };
  },

  computed: {
    ...mapState('blog', ['activeArticle', 'activeGame']),

    slug() {
      return this.$route.params.articleSlug;
    },

    img() {
      if (!this.activeArticle?.images) {
        return false;
      }
      return {
        default: `/storage${this.activeArticle.images['1300x433']}`,
        alt: this.activeArticle.image_alt || '',
        title: this.activeArticle.image_title || '',
        sources: [
          {
            media: '(-webkit-min-device-pixel-ratio: 1)',
            srcset: `/storage${this.activeArticle.images['1300x433']} 1300w,
                     /storage${this.activeArticle.images['900x300']} 900w,
                     /storage${this.activeArticle.images['450x350']} 450w,`,
          },
          {
            media: '(-webkit-min-device-pixel-ratio: 2)',
            srcset: `/storage${this.activeArticle.images['2400x800']} 1300w,
                     /storage${this.activeArticle.images['1300x433']} 900w,
                     /storage${this.activeArticle.images['900x700']} 450w,`,
          },
        ],
      };
    },

    isXurArticle() {
      return this.activeArticle.id === +process.env.DESTINY_XUR_ARTICLE_ID;
    },

    parsedContent() {
      return this.parseContent(this.activeArticle.content);
    },

    relatedArticles() {
      return this.activeArticle.related_blog_posts;
    },
  },

  seo() {
    if (!this.activeArticle) {
      return {};
    }

    const urn = typeof window === 'undefined'
      ? this.$ssrContext.url
      : window.location.pathname;

    return {
      title: this.activeArticle.seo_title || this.activeArticle.title,
      meta: {
        description: this.activeArticle.seo_meta_description || this.activeArticle.description,
        ...(this.activeArticle.blog_author ? { author: this.activeArticle.blog_author.name } : []),
        'og:type': 'website',
        'og:title': this.activeArticle.title,
        'og:url': `${AppModel.URL}${urn}`,
        'og:image': this.activeArticle?.images
          ? `${AppModel.URL}/storage${this.activeArticle.images['900x700']}`
          : null,
        'og:description': this.activeArticle.description,
      },
      article: {
        ...this.activeArticle,
        canonicalUrl: `${AppModel.URL}${urn}`,
      },
    };
  },

  created() {
    if (!this.$isSSR) {
      if (!this.activeArticle) {
        getArticle(this.$store, this.slug)
          .then(() => {
            this.setFilter();
          });
      } else {
        this.setFilter();
      }
    }
  },

  mounted() {
    localStorageRatesBySlug = JSON.parse(window.localStorage.getItem(LOCALSTORAGE_RATES_KEY)) || {};
    if (localStorageRatesBySlug[this.slug]) {
      this.changeRateState(+localStorageRatesBySlug[this.slug]);
    }

    this.incrementViews();
  },

  methods: {
    ...mapMutations('blog', ['mutateEntity']),
    ...mapMutations('products', { mutateProductsEntity: 'mutateEntity' }),

    addLinkQuery(product) {
      return {
        utm_source: 'skycoach.gg',
        utm_medium: 'blog',
        utm_content: 'watch_our_product',
        utm_campaign: this.$route.path.replace(/^\/blog/, ''),
        utm_term: product.title,
      };
    },

    cleanHTML(html) {
      if (!html) {
        return null;
      }

      let cleanedHtml = html;

      if (GameModel.LIST_WOW_ALL_IDS.includes(this.activeGame.id)) {
        cleanedHtml = cleanedHtml.replace(
          regExpWowHeadLink,
          'href="#" data-wowhead="$3&domain=$2" onclick="return false"',
        );
      } else if (this.activeGame.id === GameModel.DESTINY.id) {
        let matchedUrls = cleanedHtml.match(regExpLightGGLink);

        if (matchedUrls) {
          matchedUrls = matchedUrls.map((url) => ({
            key: url.slice(0, -1), // ключ в объекте кэша (равен href)
            link: url.replace(regExpLightGGLink, '$2hover/$4'), // прямая ссылка на тултип
          }));
        }

        cleanedHtml = cleanedHtml.replace(regExpLightGGLink, '$1" onclick="return false" rel="nofollow noopener"');

        if (matchedUrls) {
          executeAfterLoad(() => {
            requestAnimationFrame(() => {
              if (window?.preloadTooltipByUrl) {
                this.preloadDestinyTooltipsFromUrls(matchedUrls);
              } else {
                const tooltipScript = document.getElementById('destiny-tooltips');
                tooltipScript.onload = () => {
                  this.preloadDestinyTooltipsFromUrls(matchedUrls);
                };
              }
            });
          });
        }
      }

      cleanedHtml = htmlNormalizeImageRatio(cleanedHtml);

      return cleanedHtml;
    },

    setFilter() {
      const filter = {
        BlogFilterAnchors: {
          component: 'BlogFilterAnchors',
          props: {
            articleId: this.activeArticle.id,
          },
        },
      };

      this.$store.commit('blog/mutateEntity', { entity: 'filter', data: filter });
    },

    setRate(rate) {
      if (!this.activeArticle) {
        return;
      }

      if (this.slug && !this.rate.isReadOnly) {
        this.changeRateState(rate);

        this.$axios.get(`${API_URI}/blog_posts/${this.activeArticle.id}/rate?rate=${rate}`)
          .then(() => {
            localStorageRatesBySlug[this.slug] = rate;
            window.localStorage.setItem(LOCALSTORAGE_RATES_KEY, JSON.stringify(localStorageRatesBySlug));
          });
      } else {
        this.rate.value = null;
      }
    },

    changeRateState(rate) {
      this.rate.value = rate;
      this.rate.isReadOnly = true; // комментим эту строку если хотим дать возможность переставить оценку
    },

    incrementViews() {
      if (!this.activeArticle) {
        return false;
      }

      return axios.get(`${API_URI}/blog_posts/${this.activeArticle.id}/increment-views`);
    },

    preloadDestinyTooltipsFromUrls(items) {
      items.forEach(({ key, link }) => {
        window.preloadTooltipByUrl(key, link);
      });
    },

    parseContent(content) {
      // eslint-disable-next-line max-len
      const combinedRegexp = /(<div\s+class="(?:[^"]*\s)?custom-checklist(?:\s[^"]*)?">)((?:\s*<div\s+class="(?:[^"]*\s)?custom-checklist-item(?:\s[^"]*)?">.*?<\/div>\s*)*?)(<\/div>)|(<div class="article-our-products(?: mceNonEditable)?"[^>]*>([\s\S]*?)<\/div>)/gi;
      const checklistItemRegexp = /<div\s+class="(?:[^"]*\s)?custom-checklist-item(?:\s[^"]*)?">(.*?)<\/div>/gi;

      const chunks = [];
      let lastIndex = 0;

      const processMatch = (match, type) => {
        const startIndex = match.index;
        if (startIndex > lastIndex) {
          chunks.push({ type: 'html', content: content.slice(lastIndex, startIndex) });
        }

        let chunkContent;

        // Поддержка старого формата блока
        if (type === 'our-products') {
          if (match[0] === '<div class="article-our-products mceNonEditable">==Watch our products block==</div>') {
            chunkContent = this.activeArticle?.our_products?.content;
          } else {
            // eslint-disable-next-line prefer-destructuring
            chunkContent = match[0];
          }
        } else {
          // eslint-disable-next-line prefer-destructuring
          chunkContent = match[0];
        }

        chunks.push({
          type,
          content: chunkContent,
        });
        lastIndex = startIndex + match[0].length;
      };

      Array.from(content.matchAll(combinedRegexp))
        .forEach((match) => {
          if (match[1]) { // checklist match
            processMatch(match, 'checklist');

            const checklistItems = [];

            Array.from(match[2].matchAll(checklistItemRegexp))
              .forEach((itemMatch) => {
                checklistItems.push({
                  text: itemMatch[1],
                  checked: false,
                });
              });

            chunks[chunks.length - 1].checklistItems = checklistItems;
          } else if (match[4]) { // our-products match
            processMatch(match, 'our-products');

            const productIds = new Set(match[4]
              ?.match(/data-product-ids="([^"]*)"/)
              ?.[1]
              ?.split(',')
              ?.map(Number));

            const order = {};
            Array.from(productIds).forEach((id, index) => {
              order[id] = index;
            });

            chunks[chunks.length - 1].products = productIds?.size > 0
              ? this.activeArticle.products
                .filter((product) => productIds.has(product.id))
                .sort((a, b) => order[a.id] - order[b.id])
              : this.activeArticle.products.slice(0, 3);
          }
        });

      if (lastIndex < content.length) {
        chunks.push({
          type: 'html',
          content: content.slice(lastIndex),
        });
      }

      return chunks;
    },

  },
};

</script>

<style lang="scss">
.blog-article {
  line-height: 1.2;
}

.blog-article__html {
  @import "../../../../scss/textBase";
  @import "../../../../scss/articleDescriptionBase";
  @import "../../../../scss/shapes/arrowhead";

  p {
    img {
      height: auto;
    }
  }
}
</style>

<style lang="scss" scoped>
.blog-article {
  font-size: 15px;
}

.blog-article__content {
  margin-top: 32px;
}

.blog-article__title {
  font-size: 1.6667em;
  line-height: 1.2;
  font-weight: 500;
}

.blog-article__our-products {
  border-radius: 0;
  border: none;
  background: url(/images/games/backgrounds/neutral/bg-1920.jpg) no-repeat 50%;
  font-weight: 500;
}

.our-products__list {
  position: relative;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(250px, 320px);
  grid-gap: 16px;
  margin-top: 24px;
  overflow-x: auto;
}

.blog-article__image {
  height: 200px;
  position: relative;
}

.blog-article__image--has-caption {
  margin-bottom: 44px;
}

.blog-article__image-pic {
  --border-radius: 15px;
}

.blog-article__image-caption {
  position: absolute;
  bottom: -2.25em;
  font-size: 11px;
  color: $color--basic-light-gray;
}

@media screen and (max-width: 899px) {
  .blog-article {
    @include anti-content;
    padding-left: 0;
    padding-right: 0;
  }

  .blog-article__image-wrap,
  .blog-article__xur-banner,
  .blog-article__body,
  .blog-article__our-products,
  .blog-article__rate,
  .blog-article__title,
  .blog-article__you-may-like {
    @include container;
  }

  .blog-article__our-products {
    padding-top: 24px;
    padding-bottom: 24px;
  }

  .our-products__list {
    @include anti-content;
    @include hide-scrollbar;
    overflow-x: auto;
  }
}

.blog-article__our-products {
  margin-top: 32px;
}

.blog-article__title {
  margin-top: 36px;
}

.blog-article__stats {
  margin-top: 24px;
}

.blog-article__rate {
  margin-top: 16px;
}

.blog-article__you-may-like {
  margin-top: 24px;
}

@media screen and (min-width: 900px) {
  .blog-article__our-products {
    border: 1px solid $color--basic-menu;
    border-radius: 15px;
    padding: 32px;
  }

  .our-products__list {
    justify-content: center;
  }

  .blog-article__image {
    height: 305px;
  }

  .blog-article__content {
    margin-top: 36px;
  }

  .blog-article__image--has-caption {
    margin-bottom: 56px;
  }

  .blog-article__image-caption {
    font-size: 13px;
  }
}

@media screen and #{$screen-less-than-1024} {
  .blog-article__you-may-like {
    --item-width: 188px;
  }
}
</style>
